/**
 * i18next setup. Norwegian is the default language (principle 8) and the fallback: if a key
 * is somehow missing at runtime, a Norwegian sentence is a better failure than a raw key.
 */
import { DEFAULT_LOCALE, isLocale, type Locale } from '@tvitvi/shared';
import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_NAMESPACE, resources } from './resources';

export const i18n: I18nInstance = i18next.createInstance();

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: DEFAULT_NAMESPACE,
  ns: [DEFAULT_NAMESPACE],
  returnNull: false,
  interpolation: {
    // React escapes for us; double-escaping turns "Ø" into an entity in attributes.
    escapeValue: false,
  },
  react: { useSuspense: false },
});

/**
 * TYPENAVN MIDT I EN SETNING (QA13 punkt 30, ordrunden I1): «Bemanner hver bruk av bar», ikke
 * «… av Bar». Navnet er organisasjonens eget og står med stor forbokstav i lista; i en setning skrives
 * det `{{type, lowerFirst}}`. Et navn som begynner med to store bokstaver (PA, LED-vegg) er en
 * forkortelse og står som det er.
 */
export function lowerFirst(value: string): string {
  const [first, second] = [...value];
  if (first === undefined) return value;
  if (second !== undefined && second !== second.toLowerCase()) return value;
  return first.toLowerCase() + value.slice(first.length);
}
i18n.services.formatter?.add('lowerFirst', (value: unknown) =>
  typeof value === 'string' ? lowerFirst(value) : String(value),
);

/** Applies the user's stored language and keeps `<html lang>` in sync for screen readers. */
export async function applyLocale(locale: Locale): Promise<void> {
  if (i18n.language !== locale) {
    await i18n.changeLanguage(locale);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

/** The active locale, always narrowed to something we actually ship. */
export function currentLocale(): Locale {
  return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}

/**
 * NETTLESERENS SPRÅK, for sidene som ikke har en bruker å spørre (QA13 runde 2, 15d).
 *
 * ------------------------------------------------------------------------------------------------
 * Atle: «Det offentlige skjemaet er på norsk uansett språkvalg. Skal følge nb/en.»
 *
 * «Språkvalg» finnes ikke på den ruta: den besøkende følger en plakat, har ingen sesjon og ingen
 * lagret preferanse. `applyLocale` leser brukerens valg, og på en anonym rute er det ingen bruker.
 * Det eneste signalet som finnes er nettleseren, og den sender det på hver eneste forespørsel
 * ellers — så det er den vi spør.
 *
 * NORSK ER FORTSATT FALLBACKEN, som prinsipp 8 sier. En svensk eller tysk nettleser får norsk, ikke
 * engelsk: dette er et norsk produkt, og engelsk er det ene alternativet vi faktisk har oversatt.
 */
export async function applyBrowserLocale(): Promise<void> {
  const preferred = typeof navigator === 'undefined' ? [] : navigator.languages;
  /* `en-GB` og `en-US` er begge «en». Bare den første delen betyr noe for oss. */
  const found = preferred.map((tag) => tag.split('-')[0]).find((tag) => isLocale(tag));
  await applyLocale(isLocale(found) ? found : DEFAULT_LOCALE);
}
